import { useState } from 'react';
import { Link } from 'react-router-dom';
import PedalPriceDisplay from './PedalPriceDisplay';
import ActiveTraderBadge from './ActiveTraderBadge';
import ListingThumb from './ListingThumb';
import WantDemandBadge from './WantDemandBadge';
import { listingImageUrls, isPlaceholderListing, placeholderFor, SKETCH_PLACEHOLDER_URL } from '../utils/listingPlaceholder';

// currentUser/firestoreUser/myWants power the demand heart; callers own the
// single useMyWants listener per page (WANT_DEMAND_BADGE_PLAN.md). All three
// optional — omitted, the heart still renders the count and a tap routes
// through the signed-out path. showDemandBadge=false hides the heart
// entirely — the dashboard carousels opt out (Jason, 2026-09-16).
const RecommendedPedalCard = ({ pedal, currentUser = null, firestoreUser = null, myWants = [], showDemandBadge = true }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const wantedPedals = pedal.matchInfo?.wantedPedals || [];
  const wantedCount = wantedPedals.length;
  const wantedTooltip = wantedCount > 1
    ? wantedPedals.map((p) => `${p.make} ${p.model}`).join(', ')
    : undefined;

  // Real photos, or exactly one placeholder entry for a photo-less listing
  // (src/utils/listingPlaceholder.js).
  const images = listingImageUrls(pedal);
  const showPlaceholderTag = isPlaceholderListing(pedal);

  const handlePrevImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNextImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  return (
    <div
      className="block bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200 overflow-hidden"
      style={{
        width: 331,
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04)',
        border: '1px solid #e5e7eb'
      }}
    >
      <Link
        to={`/listing/${pedal.id}`}
        className="block"
        style={{ width: 331, textDecoration: 'none', color: 'inherit' }}
      >
        <div
          className="bg-gray-100 relative flex items-center justify-center"
          style={{ borderRadius: '15px', overflow: 'hidden', width: 331, height: 331, padding: '8px', position: 'relative', boxSizing: 'border-box' }}
        >
          {images.length > 0 ? (
            <>
              <ListingThumb
                src={images[currentImageIndex]}
                alt={`${pedal.make} ${pedal.model}`}
                className="w-full h-full object-cover"
                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px' }}
                key={currentImageIndex}
              />

              <style jsx>{`
                .dashboard-nav-arrow {
                  position: absolute;
                  top: 50%;
                  transform: translateY(-50%);
                  background: rgba(255, 255, 255, 0.82);
                  border: none;
                  color: #000000;
                  font-size: 26px;
                  cursor: pointer;
                  width: 38px;
                  height: 38px;
                  border-radius: 50%;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  padding: 0;
                  z-index: 2;
                  opacity: 0;
                  transition: opacity 0.2s, background 0.2s;
                  user-select: none;
                }
                .dashboard-nav-arrow-left {
                  left: 6px;
                  padding-bottom: 3px;
                }
                .dashboard-nav-arrow-right {
                  right: 6px;
                  padding-bottom: 3px;
                }
                .dashboard-nav-arrow:hover {
                  background: rgba(255, 255, 255, 0.96);
                }
                @media (hover: hover) and (pointer: fine) {
                  .dashboard-image-container:hover .dashboard-nav-arrow {
                    opacity: 1;
                  }
                }
                @media (hover: none) or (pointer: coarse) {
                  .dashboard-nav-arrow {
                    opacity: 0.9;
                  }
                }
                .dashboard-description-overlay {
                  position: absolute;
                  bottom: 8px;
                  left: 8px;
                  right: 8px;
                  background: rgba(31, 41, 55, 0.8);
                  padding: 12px 16px;
                  transform: translateY(calc(100% + 24px));
                  transition: transform 0.25s ease-in-out;
                  display: flex;
                  align-items: flex-start;
                  z-index: 3;
                  border-radius: 12px;
                  will-change: transform;
                }
                @media (hover: hover) and (pointer: fine) {
                  .dashboard-image-container:hover .dashboard-description-overlay {
                    transform: translateY(0);
                  }
                }
                .dashboard-description-text {
                  color: #ffffff;
                  font-size: 14px;
                  font-weight: bold;
                  line-height: 1.4;
                  margin: 0;
                  display: -webkit-box;
                  -webkit-line-clamp: 3;
                  line-clamp: 3;
                  -webkit-box-orient: vertical;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  word-wrap: break-word;
                  overflow-wrap: break-word;
                  max-height: calc(1.4em * 3);
                  width: 100%;
                }
              `}</style>

              <div className="dashboard-image-container" style={{ position: 'absolute', inset: 0 }}>
                <button
                  type="button"
                  className="dashboard-nav-arrow dashboard-nav-arrow-left"
                  onClick={handlePrevImage}
                  aria-label="Previous image"
                  style={{ pointerEvents: 'auto' }}
                >
                  ‹
                </button>
                <button
                  type="button"
                  className="dashboard-nav-arrow dashboard-nav-arrow-right"
                  onClick={handleNextImage}
                  aria-label="Next image"
                  style={{ pointerEvents: 'auto' }}
                >
                  ›
                </button>
                {pedal.description && currentImageIndex === 0 && (
                  <div className="dashboard-description-overlay">
                    <p className="dashboard-description-text">{pedal.description}</p>
                  </div>
                )}
              </div>

              {currentImageIndex === 0 && <ActiveTraderBadge user={pedal.owner} />}
              {currentImageIndex === 0 && showPlaceholderTag && (
                <div
                  title="The owner listed this pedal without photos. Real photos are required before an offer can be accepted."
                  style={{ position: 'absolute', top: '16px', left: '16px', background: 'rgba(255,255,255,0.95)', color: '#4A5759', fontFamily: 'Nunito, sans-serif', fontSize: '13px', fontWeight: 700, letterSpacing: '0.3px', padding: '6px 12px', borderRadius: '9999px', boxShadow: '0 2px 8px rgba(0,0,0,0.2)', zIndex: 10, pointerEvents: 'none' }}
                >
                  Photos coming
                </div>
              )}

              {currentImageIndex === 0 && wantedCount > 0 && (
                <div
                  className="dashboard-rec-card-wants-text"
                  title={wantedTooltip}
                  style={{
                    position: 'absolute',
                    bottom: '8px',
                    left: '8px',
                    right: '8px',
                    backgroundColor: 'rgba(200, 159, 60, 0.92)',
                    color: '#ffffff',
                    fontSize: '14px',
                    fontWeight: 700,
                    textAlign: 'center',
                    padding: '8px 12px',
                    borderRadius: '0 0 8px 8px',
                    fontFamily: 'Nunito, sans-serif',
                    zIndex: 2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {wantedCount === 1
                    ? `Wants your ${wantedPedals[0].make} ${wantedPedals[0].model}`
                    : `Wants ${wantedCount} of your pedals`}
                </div>
              )}

              {/* Demand heart — bottom-right; lifts above the "Wants your X"
                  ribbon when it shows (WANT_DEMAND_BADGE_PLAN.md) */}
              {showDemandBadge && currentImageIndex === 0 && (
                <WantDemandBadge
                  pedal={pedal}
                  currentUser={currentUser}
                  firestoreUser={firestoreUser}
                  myWants={myWants}
                  raised={wantedCount > 0}
                />
              )}

              <div className="absolute top-2 right-2" style={{ zIndex: 3 }}>
                {pedal.owner?.badge ? (
                  <div className="bg-yellow-400 text-black text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center border border-white">
                    {pedal.owner.badge}
                  </div>
                ) : null}
              </div>
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center" style={{ width: 331, height: 331 }}>
              <img
                src={placeholderFor(pedal).url}
                alt="Photos coming"
                className="w-full h-full"
                style={{ objectFit: 'cover', opacity: 0.85 }}
                style={{ borderRadius: '15px' }}
              />
            </div>
          )}
        </div>

        {/* Info section */}
        <div className="dashboard-pedal-card-info" style={{ backgroundColor: '#ffffff', padding: '12px 14px 14px', textAlign: 'left' }}>
          <div
            title={pedal.make}
            className="dashboard-pedal-card-brand"
            style={{ fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#9ca3af', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: '2px' }}
          >
            {pedal.make}
          </div>
          <div
            title={pedal.model}
            className="dashboard-pedal-card-model"
            style={{ fontWeight: 700, color: '#1f2937', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: '8px' }}
          >
            {pedal.model}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
            <PedalPriceDisplay
              price={Number(pedal.price)}
              previousPrice={pedal.previousPrice != null ? Number(pedal.previousPrice) : null}
              layout="row"
              className="dashboard-pedal-card-price"
              dashboardTile
            />
            <span
              className="dashboard-pedal-card-pill"
              style={{ display: 'inline-flex', alignItems: 'center', padding: '4px 10px', borderRadius: '9999px', backgroundColor: '#F3F4F6', color: '#374151', fontWeight: 700, whiteSpace: 'nowrap' }}
            >
              {pedal.condition}
            </span>
          </div>
        </div>
      </Link>
    </div>
  );
};

export default RecommendedPedalCard;
